// Phase 2 (default-to-dedupe) — the client-side "Entity Consolidation" state model.
//
// This is the pure heart of the two-panel reconcile screen: it owns the distinct-
// name grouping the user builds and produces the `groups` payload the backend
// (Reconcile::Orchestrator) commits. Kept framework-free so it can be exhaustively
// unit-tested against the narrative's workflow cases; the React panel is a thin
// renderer over it.
//
// Invariants it enforces (narrative §3.1–3.6):
//  - Pre-resolution: every distinct name's full membership + type composition is
//    fixed at init from the ENTIRE candidate set, so outcomes never depend on the
//    order the user clicks (Settled Decision #3).
//  - Deduplication is the default; a name only stays distinct (aliased) when the
//    user designates it a survivor, which costs a rationale for every survivor
//    past the first (§3.6).
//  - Individual and Organization can never share a reconciliation, even when they
//    coincidentally share a name (§3.5) — refused via the resolution sequence.

import { defaultSurvivorId } from 'pages/documents/structuring/reconcile/candidate-utils';
import type { OperationTag } from 'pages/documents/structuring/reconcile/types';

export type ConsolidationEntityType =
  'IndividualEntity' | 'OrganizationEntity' | 'ImportedEntity';

export interface ConsolidationCandidate {
  id: number;
  name: string;
  type: ConsolidationEntityType;
  // Match confidence (0–1), null for the viewed/staged entity and user-added or
  // canonized entities that have no agent score. Drives the Sort control (§3.1);
  // candidates arrive in best-match order (pinned viewed/staged, then descending
  // score, user-added behind scored, lowest id) so member array order encodes it.
  confidence?: number | null;
  // Unresolved address-confidence flag (#26): blocks submission (not selection).
  addressFlagged?: boolean;
  // §3.7: when this candidate is ITSELF a collapsed alias group (one row standing
  // in for a whole alias family), the number of entities folded behind it —
  // threaded from EntityReconcileAliasGroup.memberCount. Undefined for a plain
  // standalone entity. This is DISTINCT from the survivor-alias closure modeling
  // (aliasGroups/aliasGroupSize below): that counts co-surviving names the user
  // aliases together; this is a member that is already a collapsed family.
  aliasGroupMemberCount?: number;
  // §3.7: when this candidate is a collapsed alias group, the type breakdown of
  // the family's members (e.g. { IndividualEntity: 2 }) — what the alias family
  // actually contains, threaded from EntityReconcileAliasGroup.memberTypes. This
  // is what the right-panel "Alias Group" column reports: it describes the
  // family, not the name's own checked-member types.
  aliasGroupMemberTypes?: Partial<Record<ConsolidationEntityType, number>>;
  // Participation counts (Decision #31): distinct workspaces / interpretations
  // this entity appears in — threaded through so the panel can show name-level
  // sums. Optional + additive; absent candidates contribute 0.
  workspaceCount?: number;
  interpretationCount?: number;
  // The viewed/anchor entity that opened the reconcile screen. Pins its name to
  // the top of the right-panel list regardless of sort: an Imported viewed
  // entity has no self-confidence and otherwise sorts dead last.
  staged?: boolean;
  // Chosen from the Add Entities library search (§3.9), not part of the scored
  // suggestion set. Such entities pin to the top of both tables — right below
  // the viewed/anchor row on the left, at the head of the unactioned names on
  // the right — so a just-added entity is never buried at the bottom.
  userAdded?: boolean;
}

// Sort order for the unactioned-name section of Entity Consolidation (§3.1 / #30).
export type SortMode =
  'confidence-desc' | 'confidence-asc' | 'alpha-asc' | 'alpha-desc';

export type NameRole = 'unactioned' | 'survivor' | 'deduped';

export interface ConsolidationName {
  // Canonicalized grouping key (case-insensitive; punctuation/whitespace
  // significant — mirrors the backend Reconcile::NameEquivalence signal).
  key: string;
  // Display name (the first candidate's literal spelling for this key).
  label: string;
  memberIds: number[]; // every candidate id under this name (pre-resolved)
  checkedIds: number[]; // subset currently included in the reconciliation
  role: NameRole;
  dedupedInto: string | null; // survivor key, when role === 'deduped'
  order: number | null; // designation order (survivors only) — group 0 is the hub
  representativeId: number | null; // survivor rep override; null => computed default
  rationale: string;
  primaryAddressId: number | null;
  hidden: boolean; // an unactioned name removed from the visible list
}

export interface ConsolidationState {
  candidates: Record<number, ConsolidationCandidate>;
  // Insertion order of every candidate id (init order, then Add-Entities
  // appends). The candidates Record is unordered, so this array is the model's
  // single source of truth for display order — orderedCandidates(state) reads it.
  // Best-match order (viewed/staged pinned first, then descending score)
  // is encoded here by the caller passing candidates in that order.
  candidateOrder: number[];
  names: ConsolidationName[];
  nextOrder: number;
  // Transient refusal from the last dispatched action, folded into state so the
  // reducer serializes it instead of a side-channel. Cleared on the next
  // action that mutates the model (or via the `clearRefusal` action).
  lastRefusal: string | null;
}

export interface GroupPayload {
  survivor_id: number;
  member_ids: number[];
  rationale: string | null;
  primary_address_id: number | null;
}

export type ConsolidationAction =
  | { type: 'check'; id: number }
  | { type: 'uncheck'; id: number }
  | { type: 'toggleSurvivor'; key: string }
  | { type: 'drag'; keys: string[]; survivorKey: string }
  | { type: 'includeAll'; key: string }
  | { type: 'setRepresentative'; key: string; id: number }
  | { type: 'setRationale'; key: string; text: string }
  | { type: 'setPrimaryAddress'; key: string; addressId: number | null }
  | { type: 'removeName'; key: string }
  | { type: 'restoreName'; key: string }
  | { type: 'addCandidates'; candidates: ConsolidationCandidate[] }
  // Records the server reports merged or removed since load (a stale submit):
  // they leave the model entirely, so they can't be ticked back in.
  | { type: 'removeCandidates'; ids: number[] }
  // Records the server reports renamed since load (a stale submit), by id.
  | { type: 'renameCandidates'; names: Record<number, string> }
  | {
      type: 'canonize';
      importedId: number;
      canonical: ConsolidationCandidate;
    }
  // Dismiss the transient refusal without mutating the model.
  | { type: 'clearRefusal' }
  // Address-confidence flag resolved for an entity (#26) — drops its
  // addressFlagged so the name/submit gate updates without a full refetch.
  | { type: 'clearAddressFlag'; id: number }
  // The server rejected a submit for unresolved address review flags and named
  // the records — set addressFlagged on those candidates so the row
  // marker + submit gate light up on a set the client hadn't caught (e.g. a
  // folded family member or a flag added after load).
  | { type: 'flagAddresses'; ids: number[] };

export interface ActionResult {
  state: ConsolidationState;
  // Set when the action (or part of it) was refused — e.g. a cross-type merge.
  // The panel surfaces this to the user; the state is returned unchanged.
  refused?: string;
}

const CANONICAL_TYPES: ConsolidationEntityType[] = [
  'IndividualEntity',
  'OrganizationEntity',
];

/**
 * Whether the type is Individual or Organization (a resolved, non-imported entity).
 *
 * @param type - The entity type.
 */
const isCanonical = (type: ConsolidationEntityType) =>
  CANONICAL_TYPES.includes(type);

/**
 * A name/selection that carries both canonical types can never be brought in as a
 * whole — the cross-type name-collision refusal (§3.5).
 *
 * @param types - The entity types present.
 */
const hasBothCanonicalTypes = (
  types: Set<ConsolidationEntityType | undefined>
): boolean => types.has('IndividualEntity') && types.has('OrganizationEntity');

/**
 * Grouping key: case-fold only. Punctuation and internal whitespace stay
 * significant, matching the backend Reconcile::NameEquivalence signal (so the
 * client's distinct-name rows line up with the server's identical-name rule).
 *
 * @param name - The name row.
 */
export const nameKey = (name: string) => name.trim().toLowerCase();

/**
 * An unactioned name row with no members yet.
 *
 * @param key - The name key.
 * @param label - The name's display label.
 */
const emptyName = (key: string, label: string): ConsolidationName => ({
  key,
  label,
  memberIds: [],
  checkedIds: [],
  role: 'unactioned',
  dedupedInto: null,
  order: null,
  representativeId: null,
  rationale: '',
  primaryAddressId: null,
  hidden: false,
});

// Options for seeding the initial state so each entry point's §3.2 pre-check is
// a model-level fact rather than a post-init applyAction call by the host.
export interface InitOptions {
  // Entity ids to pre-check in order, exactly as if each had been checked in
  // turn (so the resolution sequence + cross-type guards apply). Used for the
  // §3.2 pre-checks: a canonical viewed entity (its name the initial survivor);
  // Match Review's Suggestion-1-then-staged seeding. An Imported viewed entity
  // passes nothing.
  preCheckIds?: number[];
}

/**
 * Builds the starting state: groups candidates into distinct-name rows in
 * candidate order, then applies the §3.2 pre-check ids as if each were checked
 * in turn (a refused id is skipped).
 *
 * @param candidates - The candidate rows, in display order.
 * @param options - Seeding options, e.g. the ids to pre-check.
 * @return The seeded consolidation state.
 */
export function initConsolidation(
  candidates: ConsolidationCandidate[],
  options: InitOptions = {}
): ConsolidationState {
  const byId: Record<number, ConsolidationCandidate> = {};
  const candidateOrder: number[] = [];
  const order: string[] = [];
  const grouped = new Map<string, ConsolidationName>();

  candidates.forEach((candidate) => {
    if (byId[candidate.id] === undefined) candidateOrder.push(candidate.id);
    byId[candidate.id] = candidate;
    const key = nameKey(candidate.name);
    let group = grouped.get(key);
    if (!group) {
      group = emptyName(key, candidate.name);
      grouped.set(key, group);
      order.push(key);
    }
    group.memberIds.push(candidate.id);
  });

  let state: ConsolidationState = {
    candidates: byId,
    candidateOrder,
    names: order.map((key) => grouped.get(key)!),
    nextOrder: 0,
    lastRefusal: null,
  };

  // Seed the §3.2 pre-check as a model fact. A refused seed (e.g. an
  // opposite-type id) is skipped, matching the resolution sequence.
  (options.preCheckIds ?? []).forEach((id) => {
    const result = checkEntity(state, id);
    if (!result.refused) state = result.state;
  });

  return state;
}

// ---- selectors -------------------------------------------------------------

/**
 * The name row with this key, if present.
 *
 * @param state - The consolidation state.
 * @param key - The name key.
 */
const findName = (state: ConsolidationState, key: string) =>
  state.names.find((n) => n.key === key);

/**
 * The key of the name a candidate groups under ('' for an unknown id).
 *
 * @param state - The consolidation state.
 * @param id - The candidate entity id.
 */
const nameOfEntity = (state: ConsolidationState, id: number) =>
  nameKey(state.candidates[id]?.name ?? '');

/**
 * Every checked entity id, across all name rows.
 *
 * @param state - The consolidation state.
 */
export const checkedEntityIds = (state: ConsolidationState): number[] =>
  state.names.flatMap((n) => n.checkedIds);

/**
 * Every candidate in display order. state.candidates + candidateOrder is
 * the single source of truth for which candidates are present and their order,
 * so the candidate table renders from this rather than a parallel host list.
 *
 * @param state - The consolidation state.
 */
export const orderedCandidates = (
  state: ConsolidationState
): ConsolidationCandidate[] =>
  state.candidateOrder
    .map((id) => state.candidates[id])
    .filter((c): c is ConsolidationCandidate => c !== undefined);

/**
 * Survivors in designation order (group 0 is the aliasing hub).
 *
 * @param state - The consolidation state.
 */
export const survivors = (state: ConsolidationState): ConsolidationName[] =>
  state.names
    .filter((n) => n.role === 'survivor')
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

/**
 * The survivors' name keys, in designation order.
 *
 * @param state - The consolidation state.
 */
export const survivorKeys = (state: ConsolidationState): string[] =>
  survivors(state).map((n) => n.key);

/**
 * Every checked entity type currently anywhere in the reconciliation. All
 * survivors alias together, so a canonical type here excludes its opposite.
 *
 * @param state - The consolidation state.
 * @return The checked entity types.
 */
const reconciliationTypes = (
  state: ConsolidationState
): Set<ConsolidationEntityType> => {
  const types = new Set<ConsolidationEntityType>();
  checkedEntityIds(state).forEach((id) => {
    const type = state.candidates[id]?.type;
    if (type) types.add(type);
  });
  return types;
};

/**
 * The entity types a survivor would ultimately merge together: its own checked
 * members plus every name deduped into it.
 *
 * @param state - The consolidation state.
 * @param survivorKey - Key of the target survivor.
 * @return The types across the survivor's merge set.
 */
const survivorCompositionTypes = (
  state: ConsolidationState,
  survivorKey: string
): Set<ConsolidationEntityType> => {
  const types = new Set<ConsolidationEntityType>();
  state.names
    .filter((n) => n.key === survivorKey || n.dedupedInto === survivorKey)
    .forEach((n) =>
      n.checkedIds.forEach((id) => {
        const type = state.candidates[id]?.type;
        if (type) types.add(type);
      })
    );
  return types;
};

/**
 * Would including an entity of `type` place an Individual and an Organization in
 * the same reconciliation? Unconditional first step of the resolution sequence.
 *
 * @param state - The consolidation state.
 * @param type - The entity type.
 * @return True when the type conflicts with an included canonical type.
 */
const wouldCrossType = (
  state: ConsolidationState,
  type: ConsolidationEntityType
): boolean => {
  if (!isCanonical(type)) return false;
  return reconciliationTypes(state).has(oppositeCanonical(type));
};

/**
 * The other canonical type: Organization for Individual, else Individual.
 *
 * @param type - The entity type.
 * @return The opposite canonical type.
 */
const oppositeCanonical = (
  type: ConsolidationEntityType
): ConsolidationEntityType => {
  return type === 'IndividualEntity'
    ? 'OrganizationEntity'
    : 'IndividualEntity';
};

/**
 * The single canonical type currently established in the reconciliation (a
 * checked Individual or Organization anywhere), or null. Since all survivors
 * alias together, only one canonical type can ever be established at a time.
 *
 * @param state - The consolidation state.
 * @return The established canonical type, or null.
 */
export const establishedCanonicalType = (
  state: ConsolidationState
): ConsolidationEntityType | null => {
  const types = reconciliationTypes(state);
  if (types.has('IndividualEntity')) return 'IndividualEntity';
  if (types.has('OrganizationEntity')) return 'OrganizationEntity';
  return null;
};

/**
 * Candidates locked from selection because a canonical survivor of the OPPOSITE
 * type is established (#27): they can never join, so the left panel disables
 * their checkboxes rather than refusing a click after the fact.
 *
 * @param state - The consolidation state.
 * @return Ids of the locked candidates.
 */
export const lockedEntityIds = (state: ConsolidationState): number[] => {
  const established = establishedCanonicalType(state);
  if (!established) return [];
  const opposite = oppositeCanonical(established);
  return Object.values(state.candidates)
    .filter((c) => c.type === opposite)
    .map((c) => c.id);
};

const TYPE_NOUN: Record<ConsolidationEntityType, string> = {
  IndividualEntity: 'Individual',
  OrganizationEntity: 'Organization',
  ImportedEntity: 'Imported',
};

/**
 * The persistent banner shown when a canonical type is established and the
 * opposite type appears among the candidates — explaining why those rows are
 * locked. Null when nothing is locked.
 *
 * @param state - The consolidation state.
 * @return The banner text, or null.
 */
export const lockNotice = (state: ConsolidationState): string | null => {
  const established = establishedCanonicalType(state);
  if (!established) return null;
  const opposite = oppositeCanonical(established);
  if (!Object.values(state.candidates).some((c) => c.type === opposite)) {
    return null;
  }
  const est = TYPE_NOUN[established];
  const article = est === 'Organization' ? 'an' : 'a';
  return `The current survivor is ${article} ${est} entity. ${
    TYPE_NOUN[opposite]
  }s can’t dedupe or alias into ${article} ${est}, so they’re locked. To make ${
    opposite === 'IndividualEntity' ? 'an Individual' : 'an Organization'
  } the survivor instead, deselect the ${est} survivor first.`;
};

/**
 * The best-match canonical type of a name (§3.5 election): member arrays are in
 * candidate order, so the first canonical member is the highest-ranked one.
 *
 * @param state - The consolidation state.
 * @param name - The name row.
 * @return The first canonical member's type, or null.
 */
const bestMatchCanonicalType = (
  state: ConsolidationState,
  name: ConsolidationName
): ConsolidationEntityType | null => {
  for (const id of name.memberIds) {
    const type = state.candidates[id]?.type;
    if (type && isCanonical(type)) return type;
  }
  return null;
};

/**
 * A name's confidence for sorting = the highest member score (null when none of
 * its members carry a score — those sort to the end under Confidence).
 *
 * @param state - The consolidation state.
 * @param name - The name row.
 * @return The highest member confidence, or null.
 */
export const nameConfidence = (
  state: ConsolidationState,
  name: ConsolidationName
): number | null => {
  const scores = name.memberIds
    .map((id) => state.candidates[id]?.confidence)
    .filter((c): c is number => typeof c === 'number');
  return scores.length ? Math.max(...scores) : null;
};

/**
 * Sort unactioned names for the Entity Consolidation list (§3.1 / #30). A name
 * with no confidence sorts to the end under either Confidence direction, and
 * normally by label under Alphabetical.
 *
 * @param state - The consolidation state.
 * @param names - The unactioned name rows.
 * @param mode - The sort mode.
 * @return The sorted names, staged and user-added names pinned first.
 */
export const sortUnactioned = (
  state: ConsolidationState,
  names: ConsolidationName[],
  mode: SortMode
): ConsolidationName[] => {
  let sorted: ConsolidationName[];
  if (mode === 'alpha-asc')
    sorted = [...names].sort((a, b) => a.label.localeCompare(b.label));
  else if (mode === 'alpha-desc')
    sorted = [...names].sort((a, b) => b.label.localeCompare(a.label));
  else {
    const dir = mode === 'confidence-asc' ? 1 : -1;
    sorted = [...names].sort((a, b) => {
      const ca = nameConfidence(state, a);
      const cb = nameConfidence(state, b);
      if (ca === null && cb === null) return a.label.localeCompare(b.label);
      if (ca === null) return 1;
      if (cb === null) return -1;
      return (ca - cb) * dir;
    });
  }
  // Pinning, in priority order, under every sort mode (the right-panel mirror of
  // the left table's pinning):
  //  1. the viewed/anchor (staged) name — it drives the whole screen, so it must
  //     never sort to the bottom for lacking a self-confidence score;
  //  2. names whose members are all user-added (a fresh Add-Entities name).
  const [staged, notStaged] = partition(sorted, (n) => nameIsStaged(state, n));
  const [userAdded, rest] = partition(notStaged, (n) =>
    nameIsUserAdded(state, n)
  );
  return [...staged, ...userAdded, ...rest];
};

/**
 * A name that includes the viewed/anchor (staged) entity — pinned first in the
 * unactioned list so the entity driving the reconcile never sorts to the bottom.
 *
 * @param state - The consolidation state.
 * @param name - The name row.
 */
export const nameIsStaged = (
  state: ConsolidationState,
  name: ConsolidationName
): boolean =>
  name.memberIds.some((id) => state.candidates[id]?.staged === true);

/**
 * A name that exists ONLY because the user added its member(s) via Add Entities
 * (every member userAdded) — distinct from a scored suggestion a user-added
 * entity happened to merge into, which keeps its scored position.
 *
 * @param state - The consolidation state.
 * @param name - The name row.
 */
export const nameIsUserAdded = (
  state: ConsolidationState,
  name: ConsolidationName
): boolean =>
  name.memberIds.length > 0 &&
  name.memberIds.every((id) => state.candidates[id]?.userAdded === true);

/**
 * Splits items into those that pass the predicate and those that fail it,
 * keeping their order.
 *
 * @param items - The items to split.
 * @param predicate - Decides which side each item goes to.
 * @return The passing items, then the failing ones.
 */
const partition = <T>(
  items: T[],
  predicate: (item: T) => boolean
): [T[], T[]] => {
  const yes: T[] = [];
  const no: T[] = [];
  items.forEach((item) => (predicate(item) ? yes : no).push(item));
  return [yes, no];
};

/**
 * Default representative of a survivor's OWN literal name group (§3.3): the same
 * canonical-then-lowest-id rule the rest of the app uses (defaultSurvivorId,
 * itself the FE mirror of Reconcile::Classifier#default_survivor), scoped to the
 * name's checked members. Empty group -> no representative yet.
 *
 * @param state - The consolidation state.
 * @param name - The name row.
 * @return The representative entity id, or null for an empty group.
 */
export const representativeId = (
  state: ConsolidationState,
  name: ConsolidationName
): number | null => {
  if (name.representativeId !== null) return name.representativeId;
  return name.checkedIds.length
    ? defaultSurvivorId(name.checkedIds, state.candidates)
    : null;
};

/**
 * Checked entities carrying an unresolved address-confidence flag (#26). These
 * block submission until fixed or removed; removing an entity naturally clears
 * it from this set (it's scoped to checked members), so removal satisfies the
 * block the same as clearing the flag.
 *
 * @param state - The consolidation state.
 */
export const addressFlaggedCheckedIds = (state: ConsolidationState): number[] =>
  checkedEntityIds(state).filter((id) => state.candidates[id]?.addressFlagged);

/**
 * Whether a name has any currently-checked member with an address flag — drives
 * the warning icon on that name row in Entity Consolidation.
 *
 * @param state - The consolidation state.
 * @param name - The name row.
 */
export const nameHasFlaggedMember = (
  state: ConsolidationState,
  name: ConsolidationName
): boolean =>
  name.checkedIds.some((id) => state.candidates[id]?.addressFlagged);

/**
 * The number of distinct-name rows.
 *
 * @param state - The consolidation state.
 */
export const distinctNameCount = (state: ConsolidationState): number =>
  state.names.length;

/**
 * The number of name rows not yet made a survivor or deduplicated.
 *
 * @param state - The consolidation state.
 */
export const unactionedCount = (state: ConsolidationState): number =>
  state.names.filter((n) => n.role === 'unactioned').length;

/**
 * Survivors past the first that still need a rationale — submit stays disabled
 * while any remain (§3.6, deferred rationale entry).
 *
 * @param state - The consolidation state.
 */
export const outstandingRationales = (state: ConsolidationState): number =>
  survivors(state)
    .slice(1)
    .filter((n) => n.rationale.trim() === '').length;

/**
 * Nothing to consolidate or alias: no dedupe happening and fewer than two
 * survivors (§3.7 no-op — blocked, warned proactively).
 *
 * @param state - The consolidation state.
 * @return True when submitting would change nothing.
 */
export const isNoOp = (state: ConsolidationState): boolean => {
  const survivorList = survivors(state);
  const anyDedupe = state.names.some(
    (n) => n.role === 'deduped' && n.checkedIds.length > 0
  );
  const survivorWithMerge = survivorList.some((n) => n.checkedIds.length >= 2);
  return !anyDedupe && !survivorWithMerge && survivorList.length < 2;
};

// ---- alias-group modeling --------------------------------------------------
//
// Every survivor aliases together in one hub-and-spoke closure (group 0 is the
// hub), so the reconciliation currently holds a single alias group spanning all
// survivors. Modeling it explicitly lets the panel express the §3.7 alias-group
// count column and §3.8 grouping. (The hidden-member collision *resolution* that
// would split survivors into separate forced groups stays deferred, so
// this returns at most one group today; the shape is ready for more.)
export interface AliasGroup {
  // The aliasing hub — the first-designated survivor (order 0).
  hubKey: string;
  // Every survivor key aliased together, in designation order (hub first).
  memberKeys: string[];
  // Number of survivors aliased together (== memberKeys.length).
  size: number;
}

/**
 * The alias closures in the reconciliation. Aliasing only exists with two or
 * more survivors, so a lone survivor yields no alias group (nothing is aliased).
 *
 * @param state - The consolidation state.
 * @return The alias groups (at most one today).
 */
export const aliasGroups = (state: ConsolidationState): AliasGroup[] => {
  const keys = survivorKeys(state);
  if (keys.length < 2) return [];
  return [{ hubKey: keys[0], memberKeys: keys, size: keys.length }];
};

/**
 * The alias group a survivor participates in, or null (unactioned/deduped names,
 * or a lone survivor that isn't aliased with anything).
 *
 * @param state - The consolidation state.
 * @param key - The name key.
 */
export const aliasGroupOf = (
  state: ConsolidationState,
  key: string
): AliasGroup | null =>
  aliasGroups(state).find((g) => g.memberKeys.includes(key)) ?? null;

/**
 * Number of survivors aliased together across the whole reconciliation (0 when
 * fewer than two survivors) — the §3.7 count.
 *
 * @param state - The consolidation state.
 * @return The number of aliased survivors, or 0.
 */
export const aliasGroupSize = (state: ConsolidationState): number => {
  const keys = survivorKeys(state);
  return keys.length >= 2 ? keys.length : 0;
};

// ---- operation cap ---------------------------------------------------------
//
// A single reconcile is capped at a fixed number of entities, enforced
// server-side in Reconcile::Orchestrator (it rejects an oversized `groups`
// payload regardless of what the client allows — Add Entities is the assembly
// vector). The client mirrors the ceiling so the panel can warn as a selection
// approaches it and block submit at/over it; genuinely large consolidations
// route to the bulk-dedupe tool. This value matches the backend guard.
export const MAX_OPERATION_ENTITIES = 100;

// Soft threshold at which the panel starts showing a non-blocking "approaching
// the cap" warning (still submittable up to MAX_OPERATION_ENTITIES) — kept at
// 80% of the cap.
export const OPERATION_CAP_WARNING_THRESHOLD = 80;

/**
 * The number of entities this reconcile would touch — the deduped union of every
 * survivor and its folded-in members, counted exactly as the backend counts the
 * `groups` payload (one survivor + its member_ids per group; groups are
 * disjoint). This is the quantity the server-side operation cap gates, so the
 * client mirrors buildGroupsPayload rather than a looser checked-count.
 *
 * @param state - The consolidation state.
 */
export const operationEntityCount = (state: ConsolidationState): number =>
  buildGroupsPayload(state).reduce((n, g) => n + 1 + g.member_ids.length, 0);

/**
 * Non-blocking "approaching the cap" signal for the panel: the operation
 * is in the warning band as it nears the limit. Stays true at/over the cap too,
 * so the panel shows the warning band right up to where the blocker fires.
 *
 * @param state - The consolidation state.
 */
export const isApproachingOperationCap = (state: ConsolidationState): boolean =>
  operationEntityCount(state) >= OPERATION_CAP_WARNING_THRESHOLD;

/**
 * Hard cap breached — submission is blocked (see submitBlockers 'operation-cap'),
 * mirroring the server-side orchestrator rejection.
 *
 * @param state - The consolidation state.
 */
export const exceedsOperationCap = (state: ConsolidationState): boolean =>
  operationEntityCount(state) > MAX_OPERATION_ENTITIES;

// ---- submit gating ---------------------------------------------------------

export interface SubmitBlocker {
  // Stable code for host branching; message is user-facing.
  code:
    | 'no-selection'
    | 'no-op'
    | 'outstanding-rationale'
    | 'address-flagged'
    | 'operation-cap';
  message: string;
}

/**
 * The model-level reasons submission is blocked. Host-level gates
 * (preview not yet loaded, an in-flight request) are layered on top by the
 * caller; this is the pure, testable core the Reconcile button reads.
 *
 * @param state - The consolidation state.
 * @return The blockers, empty when the model allows submission.
 */
export const submitBlockers = (state: ConsolidationState): SubmitBlocker[] => {
  const blockers: SubmitBlocker[] = [];
  if (checkedEntityIds(state).length < 2) {
    blockers.push({
      code: 'no-selection',
      message: 'Select at least two entities to reconcile.',
    });
  }
  if (isNoOp(state)) {
    blockers.push({
      code: 'no-op',
      message:
        'Nothing to consolidate yet — deduplicate a name into a survivor, or designate a second survivor to keep distinct.',
    });
  }
  const pending = outstandingRationales(state);
  if (pending > 0) {
    blockers.push({
      code: 'outstanding-rationale',
      message: `${pending} additional ${
        pending === 1 ? 'survivor needs' : 'survivors need'
      } a rationale before you can reconcile.`,
    });
  }
  const flagged = addressFlaggedCheckedIds(state).length;
  if (flagged > 0) {
    blockers.push({
      code: 'address-flagged',
      message: `${flagged} included ${
        flagged === 1 ? 'entity has' : 'entities have'
      } an unresolved address-confidence flag. Resolve or remove it before reconciling.`,
    });
  }
  // Hard cap — the server rejects an oversized `groups` payload, so block
  // submit client-side at/over the limit and route larger sets to bulk-dedupe.
  const opCount = operationEntityCount(state);
  if (opCount > MAX_OPERATION_ENTITIES) {
    blockers.push({
      code: 'operation-cap',
      message: `This reconciliation includes ${opCount} entities, over the ${MAX_OPERATION_ENTITIES}-entity limit for a single operation. Remove some entities, or use bulk deduplication for larger consolidations.`,
    });
  }
  return blockers;
};

// §3.7 per-name alias-group composition — the CORRECT §3.7 column. Reads the
// candidate-level alias data threaded from EntityReconcileAliasGroup, scoped to
// ALL of the name's members (a fixed property of the name, not the selection):
//  - aliasGroupCount: how many of the name's members are THEMSELVES collapsed
//    alias groups (each folds several underlying entities behind one row);
//  - foldedEntityCount: the total underlying entities those groups fold in
//    (sum of their memberCounts);
//  - typeBreakdown: the aggregate member type counts across every member.
// NOTE: the per-alias-family TYPE composition (how many Individuals/Orgs/Imported
// sit INSIDE each collapsed group) is not carried on the payload today — only its
// memberCount is — so the type breakdown here is over the name's own member
// candidates (all the payload supports). This is DISTINCT from aliasGroups()
// above, which models the survivor-alias closure the user authors.
export interface NameAliasComposition {
  aliasGroupCount: number;
  foldedEntityCount: number;
  typeBreakdown: Record<ConsolidationEntityType, number>;
}

/**
 * A name's alias-group composition (see NameAliasComposition): how many of its
 * members are collapsed alias groups, how many entities they fold in, and
 * those families' member types.
 *
 * @param state - The consolidation state.
 * @param name - The name row.
 * @return The name's alias-group composition.
 */
export const nameAliasComposition = (
  state: ConsolidationState,
  name: ConsolidationName
): NameAliasComposition => {
  const typeBreakdown = {
    IndividualEntity: 0,
    OrganizationEntity: 0,
    ImportedEntity: 0,
  } as Record<ConsolidationEntityType, number>;
  let aliasGroupCount = 0;
  let foldedEntityCount = 0;
  // ONLY the name's members that are themselves collapsed alias groups
  // contribute — the column describes what those alias families contain (e.g.
  // "2 Individual"), NOT the name's own checked-member types (which the Entity
  // Count column already shows). typeBreakdown is summed over the families'
  // members via the aliasGroupMemberTypes threaded from the backend.
  name.memberIds.forEach((id) => {
    const candidate = state.candidates[id];
    const familyTypes = candidate?.aliasGroupMemberTypes;
    if (!familyTypes) return;
    aliasGroupCount += 1;
    let familySize = 0;
    (Object.keys(familyTypes) as ConsolidationEntityType[]).forEach((type) => {
      const count = familyTypes[type] ?? 0;
      typeBreakdown[type] += count;
      familySize += count;
    });
    foldedEntityCount += candidate.aliasGroupMemberCount ?? familySize;
  });
  return { aliasGroupCount, foldedEntityCount, typeBreakdown };
};

// Gap 2 / Decision #31 — name-level participation totals: the sum of
// workspaceCount / interpretationCount across the name's CHECKED members. Shown
// as a non-clickable aggregate on the name row (the per-entity clickable version
// lives on the left candidate table). Absent candidate counts contribute 0.
export interface NameParticipation {
  workspaceCount: number;
  interpretationCount: number;
}

/**
 * Sums workspace and interpretation counts over a name's checked members (see
 * NameParticipation).
 *
 * @param state - The consolidation state.
 * @param name - The name row.
 * @return The summed counts.
 */
export const nameParticipation = (
  state: ConsolidationState,
  name: ConsolidationName
): NameParticipation => {
  let workspaceCount = 0;
  let interpretationCount = 0;
  name.checkedIds.forEach((id) => {
    const candidate = state.candidates[id];
    if (!candidate) return;
    workspaceCount += candidate.workspaceCount ?? 0;
    interpretationCount += candidate.interpretationCount ?? 0;
  });
  return { workspaceCount, interpretationCount };
};

/**
 * Type breakdown of a name's currently-included members, for the row's count.
 *
 * @param state - The consolidation state.
 * @param name - The name row.
 * @return Checked-member counts per entity type.
 */
export const includedTypeBreakdown = (
  state: ConsolidationState,
  name: ConsolidationName
): Record<ConsolidationEntityType, number> => {
  const counts = {
    IndividualEntity: 0,
    OrganizationEntity: 0,
    ImportedEntity: 0,
  } as Record<ConsolidationEntityType, number>;
  name.checkedIds.forEach((id) => {
    const type = state.candidates[id]?.type;
    if (type) counts[type] += 1;
  });
  return counts;
};

/**
 * The `groups` payload for the backend: one per survivor, in designation order
 * (group 0 is the aliasing hub, never carries a rationale). member_ids is every
 * other checked entity folded into the survivor — its own name plus every name
 * deduped into it — excluding the representative itself.
 *
 * @param state - The consolidation state.
 * @return One group per survivor, hub first.
 */
export function buildGroupsPayload(state: ConsolidationState): GroupPayload[] {
  return survivors(state).map((survivor, index) => {
    const repId = representativeId(state, survivor);
    // A survivor always has a representative (at minimum its own checked member).
    // A null here means the model is broken — fail loudly instead of posting
    // survivor_id: null and getting an opaque 422 back from the server.
    if (repId === null) {
      throw new Error(
        `Cannot build reconcile groups: survivor "${survivor.label}" has no representative entity.`
      );
    }
    const memberIds = state.names
      .filter((n) => n.key === survivor.key || n.dedupedInto === survivor.key)
      .flatMap((n) => n.checkedIds)
      .filter((id) => id !== repId);
    return {
      survivor_id: repId,
      member_ids: memberIds,
      rationale: index === 0 ? null : survivor.rationale.trim() || null,
      primary_address_id: survivor.primaryAddressId,
    };
  });
}

/**
 * Left-panel Source-cell operation tags per checked entity. Mirrors
 * the survivor groups one-to-one: every checked member of survivor group N reads
 * "Deduplicate N" (cool-gray, outline); that group's representative additionally
 * reads "Surviving Entity N" (high-contrast). Numbering is always shown, in
 * designation order (even a lone group is "Deduplicate 1"). Unchecked entities
 * get no tag.
 *
 * @param state - The consolidation state.
 * @return The tags for each checked entity id.
 */
export const buildOperationTags = (
  state: ConsolidationState
): Map<number, OperationTag[]> => {
  const map = new Map<number, OperationTag[]>();
  /**
   * Appends a tag to an entity's tags, starting its list on first use.
   *
   * @param id - The entity id.
   * @param tag - The tag to append.
   */
  const push = (id: number, tag: OperationTag) => {
    const arr = map.get(id);
    if (arr) arr.push(tag);
    else map.set(id, [tag]);
  };
  survivors(state).forEach((survivor, index) => {
    const groupNumber = index + 1;
    const repId = representativeId(state, survivor);
    // Every checked entity that folds into this survivor — its own name plus
    // every name deduped into it (the same union buildGroupsPayload commits).
    state.names
      .filter((n) => n.key === survivor.key || n.dedupedInto === survivor.key)
      .flatMap((n) => n.checkedIds)
      .forEach((id) =>
        push(id, {
          label: `Deduplicate ${groupNumber}`,
          type: 'cool-gray',
          outline: true,
        })
      );
    if (repId !== null) {
      push(repId, {
        label: `Surviving Entity ${groupNumber}`,
        type: 'high-contrast',
      });
    }
  });
  return map;
};

// ---- reducer ---------------------------------------------------------------

/**
 * Copies the state and every name row (and its checkedIds) so the reducer can
 * mutate the copy in place.
 *
 * @param state - The consolidation state.
 */
const cloneNames = (state: ConsolidationState): ConsolidationState => ({
  ...state,
  names: state.names.map((n) => ({ ...n, checkedIds: [...n.checkedIds] })),
});

/**
 * Structural-sharing update for a single-name edit: only the target name
 * object is replaced; every other name keeps its reference, so React.memo'd rows
 * for unrelated names don't re-render. Use for edits that touch exactly one name
 * (rationale/primary-address/representative). No-op when the key is absent.
 *
 * @param state - The consolidation state.
 * @param key - The name key.
 * @param updater - Returns the replacement name row.
 * @return The updated state, or the same state when the key is absent.
 */
const updateName = (
  state: ConsolidationState,
  key: string,
  updater: (name: ConsolidationName) => ConsolidationName
): ConsolidationState => {
  let changed = false;
  const names = state.names.map((n) => {
    if (n.key !== key) return n;
    changed = true;
    return updater(n);
  });
  return changed ? { ...state, names } : state;
};

/**
 * Where a newly-actioned canonical/imported entity lands (§3.5 resolution
 * sequence step 2/3), given the categorical check already cleared. Returns the
 * survivor key to dedupe into, or null meaning "promote to a new survivor".
 *
 * @param state - The consolidation state.
 * @param type - The entity type.
 * @return The survivor key to dedupe into, or null to promote.
 */
const resolvePlacement = (
  state: ConsolidationState,
  type: ConsolidationEntityType
): string | null => {
  const ordered = survivors(state).reverse(); // newest designated first
  if (ordered.length === 0) return null; // no survivor yet -> promote (becomes first)
  if (!isCanonical(type)) return ordered[0].key; // imported -> newest survivor

  const compatible = ordered.find((s) =>
    survivorCompositionTypes(state, s.key).has(type)
  );
  return compatible ? compatible.key : null; // else promote to a new aliased survivor
};

/**
 * Makes the name the next survivor in designation order.
 *
 * @param next - The cloned state, mutated in place.
 * @param name - The name row to promote, in the cloned state.
 */
const designateSurvivor = (
  next: ConsolidationState,
  name: ConsolidationName
) => {
  name.role = 'survivor';
  name.dedupedInto = null;
  name.order = next.nextOrder;
  next.nextOrder += 1;
};

/**
 * Check a single entity. A single check never cascades to same-name peers
 * (Settled Decision #4); it only actions the name (via the resolution sequence)
 * when it is the first entity checked under a not-yet-actioned name.
 *
 * @param state - The consolidation state.
 * @param id - The candidate entity id.
 * @return The next state, or the unchanged state plus the refusal message.
 */
function checkEntity(state: ConsolidationState, id: number): ActionResult {
  const candidate = state.candidates[id];
  if (!candidate) return { state };
  const key = nameKey(candidate.name);
  const before = findName(state, key);
  if (!before || before.checkedIds.includes(id)) return { state };

  // Step 1 — categorical check (unconditional, first).
  if (wouldCrossType(state, candidate.type)) {
    return {
      state,
      refused:
        'An Individual and an Organization can’t be reconciled together. Select entities of a single type.',
    };
  }

  const next = cloneNames(state);
  const name = findName(next, key)!;
  name.hidden = false;
  const firstCheck = name.checkedIds.length === 0;
  name.checkedIds.push(id);

  if (firstCheck) {
    // Steps 2/3 — place the newly-actioned name.
    const placement = resolvePlacement(next, candidate.type);
    if (placement === null) {
      designateSurvivor(next, name);
    } else {
      name.role = 'deduped';
      name.dedupedInto = placement;
      name.order = null;
    }
  }
  return { state: next };
}

/**
 * Uncheck a single entity. A name left with no checked members resets to
 * unactioned, taking its deduplicated names with it if it was a survivor.
 *
 * @param state - The consolidation state.
 * @param id - The candidate entity id.
 * @return The next state.
 */
function uncheckEntity(state: ConsolidationState, id: number): ActionResult {
  const key = nameOfEntity(state, id);
  const before = findName(state, key);
  if (!before?.checkedIds.includes(id)) return { state };

  const next = cloneNames(state);
  const name = findName(next, key)!;
  name.checkedIds = name.checkedIds.filter((x) => x !== id);
  if (name.representativeId === id) name.representativeId = null;

  if (name.checkedIds.length === 0) {
    resetName(next, name);
  }
  return { state: next };
}

/**
 * Reset a name (and, if it was a survivor, every name deduped into it) to
 * unactioned. The confirmation prompt for "last entity of a survivor with
 * dependents" is handled in the panel; by the time this runs the user confirmed.
 *
 * @param state - The cloned state, mutated in place.
 * @param name - The name row to reset, in the cloned state.
 */
function resetName(state: ConsolidationState, name: ConsolidationName) {
  if (name.role === 'survivor') {
    state.names
      .filter((n) => n.dedupedInto === name.key)
      .forEach((n) => {
        n.role = 'unactioned';
        n.dedupedInto = null;
        n.checkedIds = [];
        n.representativeId = null;
      });
  }
  name.role = 'unactioned';
  name.dedupedInto = null;
  name.order = null;
  name.representativeId = null;
  // The additional-survivor rationale persists per name for the whole session —
  // a full reset must NOT clear it, so it survives being reset and re-designated
  // a survivor.
  name.primaryAddressId = null;
  name.checkedIds = [];
}

/**
 * Whether removing the last entity from a survivor would orphan dependents — the
 * panel confirms with the user before dispatching the uncheck/removeName.
 *
 * @param state - The consolidation state.
 * @param key - The name key.
 * @return True when the survivor has checked names deduplicated into it.
 */
export const survivorHasDependents = (
  state: ConsolidationState,
  key: string
): boolean => {
  const name = findName(state, key);
  return (
    !!name &&
    name.role === 'survivor' &&
    state.names.some((n) => n.dedupedInto === key && n.checkedIds.length > 0)
  );
};

/**
 * Whether unchecking `id` (a left-panel deselect) would empty a survivor that
 * still has deduplicated names beneath it — the §3.3 case that must show the
 * "reset the survivor and every deduplicated name beneath it" confirmation
 * before it runs. The host intercepts the uncheck and prompts when this is true.
 *
 * @param state - The consolidation state.
 * @param id - The candidate entity id.
 * @return True when the uncheck needs the reset confirmation.
 */
export const uncheckWouldResetSurvivorWithDependents = (
  state: ConsolidationState,
  id: number
): boolean => {
  const key = nameOfEntity(state, id);
  const name = findName(state, key);
  return (
    !!name &&
    name.role === 'survivor' &&
    name.checkedIds.length === 1 &&
    name.checkedIds[0] === id &&
    survivorHasDependents(state, key)
  );
};

/**
 * Bulk-bring a name in as a survivor. For a cross-type name collision (§3.5 / #27)
 * the action is NOT refused: the type incompatible with the resulting survivor is
 * automatically omitted (those members stay unchecked — and locked, since a
 * canonical survivor of the winning type now exists). Which type wins is the
 * established canonical type if one exists, else the name's best-match canonical.
 *
 * @param state - The consolidation state.
 * @param key - The name key.
 * @return The next state, or the unchanged state plus the refusal message.
 */
function toggleSurvivor(state: ConsolidationState, key: string): ActionResult {
  const before = findName(state, key);
  if (!before) return { state };

  if (before.role === 'survivor') {
    // Toggling off a survivor resets it (and its dependents).
    const next = cloneNames(state);
    resetName(next, findName(next, key)!);
    return { state: next };
  }

  // Already-actioned name (a partial OR full selection): promoting it to a
  // survivor in its own right preserves EXACTLY the current selection (§3.3) —
  // it must not silently pull in the name's other, unchecked entities. The
  // explicit "Include all" action is the way to bring the rest in. Its checked
  // members already cleared the categorical guard when they were checked, so no
  // re-resolution is needed here.
  if (before.checkedIds.length > 0) {
    const next = cloneNames(state);
    designateSurvivor(next, findName(next, key)!);
    return { state: next };
  }

  const memberTypes = new Set(
    before.memberIds.map((id) => state.candidates[id]?.type)
  );
  const collision = hasBothCanonicalTypes(memberTypes);
  const winning = collision
    ? (establishedCanonicalType(state) ?? bestMatchCanonicalType(state, before))
    : ([...memberTypes].find(
        (t): t is ConsolidationEntityType => !!t && isCanonical(t)
      ) ?? null);

  // A pure single-type name still can't join if it's the type opposite an
  // already-established survivor (its rows are locked; this guards the toggle).
  if (!collision && winning && wouldCrossType(state, winning)) {
    return {
      state,
      refused:
        'An Individual and an Organization can’t be reconciled together. Select entities of a single type.',
    };
  }

  const next = cloneNames(state);
  const name = findName(next, key)!;
  // Collision → include only the winning canonical type + Imported (which pairs
  // with either); the opposite canonical type is omitted. Otherwise include all.
  name.checkedIds = collision
    ? name.memberIds.filter((id) => {
        const type = state.candidates[id]?.type;
        return type === winning || type === 'ImportedEntity';
      })
    : [...name.memberIds];
  name.hidden = false;
  designateSurvivor(next, name);
  return { state: next };
}

/**
 * Include every remaining member of an already-actioned name (§3.7 "(include
 * all)"). Adds the unchecked members; no structural change to the name's role.
 *
 * @param state - The consolidation state.
 * @param key - The name key.
 * @return The next state, or the unchanged state plus the refusal message.
 */
function includeAll(state: ConsolidationState, key: string): ActionResult {
  const before = findName(state, key);
  if (!before) return { state };
  const memberTypes = new Set(
    before.memberIds.map((id) => state.candidates[id]?.type)
  );
  if (hasBothCanonicalTypes(memberTypes)) {
    // Include All stays refused on a cross-type collision (#27): unlike toggle or
    // drag, it has no target or established survivor to resolve which type wins.
    return {
      state,
      refused:
        'This name has both an Individual and an Organization, so “Include All” can’t choose which to keep. Use the survivor toggle or the individual checkmarks instead.',
    };
  }
  const next = cloneNames(state);
  const name = findName(next, key)!;
  const wasUnactioned = name.checkedIds.length === 0;
  name.checkedIds = [...name.memberIds];
  name.hidden = false;
  if (wasUnactioned) {
    const type = state.candidates[name.memberIds[0]]?.type;
    const placement = type ? resolvePlacement(next, type) : null;
    if (placement === null) designateSurvivor(next, name);
    else {
      name.role = 'deduped';
      name.dedupedInto = placement;
    }
  }
  return { state: next };
}

// Pure validation of a drag: whether every dragged name can be deduplicated into
// the target survivor (§3.4 all-or-nothing) and, if so, which members each
// contributes (a collision name contributes only the target-compatible type).
// Shared by `drag` (to apply) and `canDrop` (to grey out invalid targets on drag
// start), so the drop rule lives in one place.
type DragValidation =
  | { ok: true; inclusion: Map<string, number[]> }
  | { ok: false; refused: string };

/**
 * Checks a drop of names onto a survivor and works out the members each name
 * contributes (see DragValidation).
 *
 * @param state - The consolidation state.
 * @param keys - Keys of the dragged names.
 * @param survivorKey - Key of the target survivor.
 * @return The included members per name, or the refusal message.
 */
function validateDrag(
  state: ConsolidationState,
  keys: string[],
  survivorKey: string
): DragValidation {
  const target = findName(state, survivorKey);
  if (target?.role !== 'survivor') {
    return {
      ok: false,
      refused: 'That name can’t be deduplicated into the target.',
    };
  }
  if (keys.length === 0) {
    return { ok: false, refused: 'Nothing to deduplicate.' };
  }
  const targetTypes = survivorCompositionTypes(state, survivorKey);
  const targetIsCanonical = [...targetTypes].some(isCanonical);
  const targetCanonical = [...targetTypes].find(isCanonical) ?? null;

  const inclusion = new Map<string, number[]>();
  for (const key of keys) {
    const name = findName(state, key);
    if (!name || name.key === survivorKey || name.role === 'survivor') {
      return {
        ok: false,
        refused: 'That name can’t be deduplicated into the target.',
      };
    }
    const types = new Set(
      name.memberIds.map((id) => state.candidates[id]?.type)
    );
    const hasCanonical = [...types].some(isCanonical);
    // A canonical entity can never merge into an all-Imported survivor (§3.4).
    if (hasCanonical && !targetIsCanonical) {
      return {
        ok: false,
        refused:
          'A resolved entity can’t be deduplicated into a survivor made up only of imported entities.',
      };
    }
    if (hasBothCanonicalTypes(types)) {
      // Cross-type collision (#27): keep only the target's canonical type +
      // Imported; the opposite type is omitted rather than refusing the drop.
      inclusion.set(
        key,
        name.memberIds.filter((id) => {
          const t = state.candidates[id]?.type;
          return t === targetCanonical || t === 'ImportedEntity';
        })
      );
    } else {
      const singleCanonical = [...types].find(isCanonical) ?? null;
      if (
        singleCanonical &&
        targetCanonical &&
        singleCanonical !== targetCanonical
      ) {
        return {
          ok: false,
          refused:
            'An Individual and an Organization can’t be reconciled together.',
        };
      }
      inclusion.set(key, [...name.memberIds]);
    }
  }
  return { ok: true, inclusion };
}

/**
 * Pure predicate: can this set of names drop onto this survivor? Drives the
 * grey-out of invalid drop targets on drag start (the panel calls it per
 * survivor for the currently-dragged keys). Same rule the drop enforces.
 *
 * @param state - The consolidation state.
 * @param keys - Keys of the dragged names.
 * @param survivorKey - Key of the target survivor.
 */
export const canDrop = (
  state: ConsolidationState,
  keys: string[],
  survivorKey: string
): boolean => validateDrag(state, keys, survivorKey).ok;

/**
 * Dedupe one or more names into a survivor (drag & drop, §3.4). All-or-nothing:
 * if any dragged name is incompatible with the target, none apply.
 *
 * @param state - The consolidation state.
 * @param keys - Keys of the dragged names.
 * @param survivorKey - Key of the target survivor.
 * @return The next state, or the unchanged state plus the refusal message.
 */
function drag(
  state: ConsolidationState,
  keys: string[],
  survivorKey: string
): ActionResult {
  const validation = validateDrag(state, keys, survivorKey);
  if (!validation.ok) return { state, refused: validation.refused };
  const { inclusion } = validation;

  const next = cloneNames(state);
  keys.forEach((key) => {
    const name = findName(next, key)!;
    name.checkedIds = inclusion.get(key)!;
    name.role = 'deduped';
    name.dedupedInto = survivorKey;
    name.order = null;
    name.hidden = false;
  });
  return { state: next };
}

/**
 * Add entities pulled in via "Add Entities" (§3.9). They join the candidate set
 * and their names are actioned in ascending entity-ID order — appended after
 * whatever is already actioned — exactly as if each had been checked in turn
 * (so the type-compatibility resolution sequence applies; an incompatible one is
 * left unchecked). Already-present ids are ignored.
 *
 * @param state - The consolidation state.
 * @param candidates - The added entities.
 * @return The next state.
 */
function addCandidates(
  state: ConsolidationState,
  candidates: ConsolidationCandidate[]
): ActionResult {
  let next: ConsolidationState = {
    ...state,
    candidates: { ...state.candidates },
    candidateOrder: [...state.candidateOrder],
    names: state.names.map((n) => ({ ...n, checkedIds: [...n.checkedIds] })),
  };
  const newIds: number[] = [];
  candidates.forEach((candidate) => {
    if (next.candidates[candidate.id]) return;
    next.candidates[candidate.id] = candidate;
    next.candidateOrder.push(candidate.id);
    const key = nameKey(candidate.name);
    let group = next.names.find((n) => n.key === key);
    if (!group) {
      group = emptyName(key, candidate.name);
      next.names.push(group);
    }
    group.memberIds.push(candidate.id);
    newIds.push(candidate.id);
  });
  newIds.sort((a, b) => a - b);
  newIds.forEach((id) => {
    const result = checkEntity(next, id);
    if (!result.refused) next = result.state;
  });
  return { state: next };
}

/**
 * Removes retired records from the reconciliation.
 * Each retired record is unchecked first (so a survivor it leaves empty resets
 * with its dependents, exactly as a user uncheck would), then dropped along with
 * any name it leaves without members.
 *
 * @param state - The consolidation state.
 * @param ids - Ids of the candidates to remove.
 * @return The next state.
 */
function removeCandidates(
  state: ConsolidationState,
  ids: number[]
): ActionResult {
  const gone = new Set(ids.filter((id) => state.candidates[id]));
  if (gone.size === 0) return { state };
  let next = state;
  gone.forEach((id) => {
    next = uncheckEntity(next, id).state;
  });
  const candidates = { ...next.candidates };
  gone.forEach((id) => delete candidates[id]);
  return {
    state: {
      ...next,
      candidates,
      candidateOrder: next.candidateOrder.filter((id) => !gone.has(id)),
      names: next.names
        .map((n) => ({
          ...n,
          memberIds: n.memberIds.filter((id) => !gone.has(id)),
        }))
        .filter((n) => n.memberIds.length > 0),
    },
  };
}

/**
 * Applies server-side renames to candidates.
 * A spelling change within the same name only relabels it. A record renamed to
 * a different name moves to that name's row: it is unchecked first (exactly as a
 * user uncheck, so an emptied survivor resets) and checked again after the move,
 * so the resolution sequence places it under its new name.
 *
 * @param state - The consolidation state.
 * @param names - New names keyed by entity id.
 * @return The next state.
 */
function renameCandidates(
  state: ConsolidationState,
  names: Record<number, string>
): ActionResult {
  const renamed = Object.entries(names)
    .map(([id, name]) => [Number(id), name] as const)
    .filter(
      ([id, name]) =>
        state.candidates[id] !== undefined && state.candidates[id].name !== name
    );
  if (renamed.length === 0) return { state };

  const recheck: number[] = [];
  let next = state;
  renamed.forEach(([id, name]) => {
    if (nameKey(name) === nameOfEntity(next, id)) return;
    const result = uncheckEntity(next, id);
    if (result.state !== next) recheck.push(id);
    next = result.state;
  });

  const candidates = { ...next.candidates };
  const nextNames = next.names.map((n) => ({
    ...n,
    memberIds: [...n.memberIds],
  }));
  renamed.forEach(([id, name]) => {
    const from = nextNames.find((n) => n.key === nameKey(candidates[id].name))!;
    candidates[id] = { ...candidates[id], name };
    const key = nameKey(name);
    if (from.key === key) return;
    from.memberIds = from.memberIds.filter((x) => x !== id);
    let to = nextNames.find((n) => n.key === key);
    if (!to) {
      to = emptyName(key, name);
      nextNames.push(to);
    }
    to.memberIds.push(id);
  });
  next = {
    ...next,
    candidates,
    names: nextNames
      .filter((n) => n.memberIds.length > 0)
      .map((n) => ({ ...n, label: candidates[n.memberIds[0]].name })),
  };

  recheck.forEach((id) => {
    const result = checkEntity(next, id);
    if (!result.refused) next = result.state;
  });
  return { state: next };
}

/**
 * Post-conversion repositioning for "Add as New" (§3.9). The imported entity has
 * been retired server-side into a brand-new canonical entity (its own committed
 * transaction), so drop the imported from the model and add the canonical as a
 * survivor in its own right (checked). Any name left empty by the removal is
 * dropped; a survivor/deduped name that loses its last checked member resets.
 *
 * @param state - The consolidation state.
 * @param importedId - Id of the imported entity that was converted.
 * @param canonical - The new canonical entity's candidate.
 * @return The next state.
 */
function canonize(
  state: ConsolidationState,
  importedId: number,
  canonical: ConsolidationCandidate
): ActionResult {
  const next: ConsolidationState = {
    ...state,
    candidates: { ...state.candidates },
    candidateOrder: state.candidateOrder.filter((id) => id !== importedId),
    names: state.names.map((n) => ({
      ...n,
      memberIds: [...n.memberIds],
      checkedIds: [...n.checkedIds],
    })),
  };
  delete next.candidates[importedId];
  next.names.forEach((n) => {
    n.memberIds = n.memberIds.filter((id) => id !== importedId);
    n.checkedIds = n.checkedIds.filter((id) => id !== importedId);
    if (n.representativeId === importedId) n.representativeId = null;
  });
  next.names = next.names.filter((n) => n.memberIds.length > 0);
  next.names
    .filter((n) => n.role !== 'unactioned' && n.checkedIds.length === 0)
    .forEach((n) => resetName(next, n));

  next.candidates[canonical.id] = canonical;
  if (!next.candidateOrder.includes(canonical.id))
    next.candidateOrder.push(canonical.id);
  const key = nameKey(canonical.name);
  let group = next.names.find((n) => n.key === key);
  if (!group) {
    group = emptyName(key, canonical.name);
    next.names.push(group);
  }
  if (!group.memberIds.includes(canonical.id))
    group.memberIds.push(canonical.id);
  if (!group.checkedIds.includes(canonical.id))
    group.checkedIds.push(canonical.id);
  if (group.role !== 'survivor') designateSurvivor(next, group);

  // If the converted imported was itself a survivor, removing its (sole) member
  // dropped its name — leaving every name deduplicated into it pointing at a
  // now-gone survivor key: orphaned (still checked on the left, but rendered
  // under no survivor on the right). Re-home each such orphan onto the NEW
  // canonical survivor, which replaces the imported as their survivor, so
  // Add-as-New on a survivor keeps the nested names in sync.
  const survivors = new Set(survivorKeys(next));
  next.names.forEach((n) => {
    if (
      n.role === 'deduped' &&
      n.dedupedInto !== null &&
      !survivors.has(n.dedupedInto)
    ) {
      n.dedupedInto = key;
    }
  });
  return { state: next };
}

/**
 * Applies one consolidation action.
 *
 * @param state - The consolidation state.
 * @param action - The action to apply.
 * @return The next state, or the unchanged state plus the refusal message.
 */
export function applyAction(
  state: ConsolidationState,
  action: ConsolidationAction
): ActionResult {
  switch (action.type) {
    case 'check':
      return checkEntity(state, action.id);
    case 'uncheck':
      return uncheckEntity(state, action.id);
    case 'toggleSurvivor':
      return toggleSurvivor(state, action.key);
    case 'drag':
      return drag(state, action.keys, action.survivorKey);
    case 'includeAll':
      return includeAll(state, action.key);
    case 'addCandidates':
      return addCandidates(state, action.candidates);
    case 'removeCandidates':
      return removeCandidates(state, action.ids);
    case 'renameCandidates':
      return renameCandidates(state, action.names);
    case 'canonize':
      return canonize(state, action.importedId, action.canonical);
    // Single-name edits: structural-sharing update so unrelated name rows keep
    // their references and don't re-render.
    case 'setRepresentative':
      return {
        state: updateName(state, action.key, (n) => ({
          ...n,
          representativeId: action.id,
        })),
      };
    case 'setRationale':
      return {
        state: updateName(state, action.key, (n) => ({
          ...n,
          rationale: action.text,
        })),
      };
    case 'setPrimaryAddress':
      return {
        state: updateName(state, action.key, (n) => ({
          ...n,
          primaryAddressId: action.addressId,
        })),
      };
    case 'clearRefusal':
      return { state };
    case 'clearAddressFlag': {
      const candidate = state.candidates[action.id];
      if (!candidate?.addressFlagged) return { state };
      return {
        state: {
          ...state,
          candidates: {
            ...state.candidates,
            [action.id]: { ...candidate, addressFlagged: false },
          },
        },
      };
    }
    case 'flagAddresses': {
      const updates: Record<number, ConsolidationCandidate> = {};
      action.ids.forEach((id) => {
        const candidate = state.candidates[id];
        if (candidate && !candidate.addressFlagged) {
          updates[id] = { ...candidate, addressFlagged: true };
        }
      });
      if (Object.keys(updates).length === 0) return { state };
      return {
        state: {
          ...state,
          candidates: { ...state.candidates, ...updates },
        },
      };
    }
    case 'removeName': {
      const before = findName(state, action.key);
      if (!before) return { state };
      if (before.role === 'unactioned') {
        const next = cloneNames(state);
        findName(next, action.key)!.hidden = true;
        return { state: next };
      }
      const next = cloneNames(state);
      resetName(next, findName(next, action.key)!);
      return { state: next };
    }
    case 'restoreName': {
      const before = findName(state, action.key);
      if (!before?.hidden) return { state };
      const next = cloneNames(state);
      findName(next, action.key)!.hidden = false;
      return { state: next };
    }
    default:
      return { state };
  }
}

/**
 * useReducer-friendly wrapper: folds the transient refusal INTO state so
 * dispatches serialize and the refusal stops being a host side-channel. On a
 * refused action the model is unchanged but `lastRefusal` is set; on a
 * successful action a stale refusal is cleared. Referentially stable on no-ops.
 *
 * @param state - The consolidation state.
 * @param action - The action to apply.
 * @return The next state.
 */
export function consolidationReducer(
  state: ConsolidationState,
  action: ConsolidationAction
): ConsolidationState {
  const { state: nextState, refused } = applyAction(state, action);
  if (refused) {
    return state.lastRefusal === refused
      ? state
      : { ...state, lastRefusal: refused };
  }
  if (nextState === state) {
    return state.lastRefusal === null ? state : { ...state, lastRefusal: null };
  }
  return nextState.lastRefusal === null
    ? nextState
    : { ...nextState, lastRefusal: null };
}
